import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

interface ToggleLivePayload {
  sermonId: string;
  makeLive: boolean;
  questionsEnabled?: boolean;
}

interface SetLiveQuestionsPayload {
  sermonId: string;
  questionsEnabled: boolean;
}

export const useToggleLiveSermon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sermonId, makeLive, questionsEnabled = false }: ToggleLivePayload) => {
      const functionName = makeLive ? 'start_live_sermon' : 'stop_live_sermon';
      const functionPayload = makeLive
        ? { sermon_id: sermonId, questions_enabled: questionsEnabled }
        : { sermon_id: sermonId };

      const primaryAttempt = await supabase.rpc(functionName, functionPayload);
      if (primaryAttempt.error) {
        const fallbackAttempt = await supabase.rpc(functionName, makeLive
          ? { p_sermon_id: sermonId, questions_enabled: questionsEnabled }
          : { p_sermon_id: sermonId });
        if (fallbackAttempt.error) {
          throw fallbackAttempt.error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
    },
  });
};

export const useSetLiveSermonQuestionsEnabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sermonId, questionsEnabled }: SetLiveQuestionsPayload) => {
      const { error } = await supabase
        .from('sermons')
        .update({ questions_enabled: questionsEnabled })
        .eq('id', sermonId)
        .eq('is_live', true);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
      queryClient.invalidateQueries({ queryKey: ['sermons', 'live'] });
      queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
    },
  });
};
